package com.rosario.juridico.marketplace.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.LazyRow
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ExitToApp
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.navigation.NavController
import com.rosario.juridico.marketplace.model.Consulta
import com.rosario.juridico.marketplace.viewmodel.MarketplaceViewModel
import com.rosario.juridico.marketplace.viewmodel.ConsultasFeedViewModel
import com.rosario.juridico.marketplace.viewmodel.UiState
import com.rosario.juridico.navigation.Routes
import com.rosario.juridico.platform.FirebaseProxy
import com.rosario.juridico.domain.ConsultaDataModel
import com.rosario.juridico.domain.AppUsageManager

import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

private val VerdeAbogado = Color(0xFF1D9E75)

@Composable
fun ListadoConsultasScreen(navController: NavController, viewModel: MarketplaceViewModel, feedViewModel: ConsultasFeedViewModel) {
    val feedState          by feedViewModel.feedConsultasState.collectAsState()
    val isSubscribed       by viewModel.isUsuarioAbogadoSuscrito.collectAsState()
    val isLoggedIn         by viewModel.isLoggedIn.collectAsState()
    val isEmailVerified    by viewModel.isEmailVerified.collectAsState()
    val currentUserAbogado by viewModel.currentUserAbogado.collectAsState()

    val filtroTipoConsulta by feedViewModel.filtroTipoConsulta.collectAsState()

    // Refrescar auth al entrar (cubre el caso de volver del login)
    LaunchedEffect(Unit) {
        viewModel.refreshAuthState()
        feedViewModel.loadConsultasAbiertas()
    }

    LaunchedEffect(filtroTipoConsulta) {
        feedViewModel.loadConsultasAbiertas()
    }

    Column(modifier = Modifier.fillMaxSize()) {

        // ── Header con info de usuario activo y acción Cambiar Profesional ──────
        val abogMatricula = currentUserAbogado?.matricula?.ifBlank { null } ?: "Desconocida"

        MktHeader(
            titulo    = "Consultas Disponibles",
            subtitulo = "Matrícula: $abogMatricula • ${if (isSubscribed) "Acceso Premium Activo" else "Modo Lectura"}",
            onBack    = { navController.popBackStack() },
            acciones  = {
                if (isLoggedIn) {
                    TextButton(
                        onClick = {
                            viewModel.cambiarProfesional {
                                navController.navigate("onboarding_rol?role=abogado&editMode=true") {
                                    popUpTo(Routes.LISTADO_CONSULTAS) { inclusive = true }
                                }
                            }
                        },
                        colors = ButtonDefaults.textButtonColors(contentColor = Color.White)
                    ) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ExitToApp,
                            contentDescription = "Cambiar Profesional",
                            tint = Color.White,
                            modifier = Modifier.size(18.dp)
                        )
                        Spacer(Modifier.width(4.dp))
                        Text(
                            text = "Cambiar",
                            color = Color.White,
                            fontSize = 12.sp,
                            fontWeight = FontWeight.Bold
                        )
                    }
                }
            }
        )

        // Banner informativo del Profesional Activo
        if (isLoggedIn && currentUserAbogado != null) {
            Surface(
                color = VerdeAbogado.copy(alpha = 0.1f),
                modifier = Modifier.fillMaxWidth()
            ) {
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(horizontal = 16.dp, vertical = 6.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = "Profesional: Matrícula ${currentUserAbogado?.matricula?.ifBlank { "Desconocida" } ?: "Desconocida"}",
                        style = MaterialTheme.typography.labelMedium,
                        fontWeight = FontWeight.Bold,
                        color = VerdeAbogado
                    )
                    TextButton(
                        onClick = {
                            viewModel.cambiarProfesional {
                                navController.navigate("onboarding_rol?role=abogado&editMode=true") {
                                    popUpTo(Routes.LISTADO_CONSULTAS) { inclusive = true }
                                }
                            }
                        },
                        contentPadding = PaddingValues(horizontal = 4.dp, vertical = 0.dp)
                    ) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ExitToApp,
                            contentDescription = null,
                            tint = VerdeAbogado,
                            modifier = Modifier.size(14.dp)
                        )
                        Spacer(Modifier.width(4.dp))
                        Text(
                            text = "Cambiar Profesional",
                            style = MaterialTheme.typography.labelSmall,
                            fontWeight = FontWeight.Bold,
                            color = VerdeAbogado
                        )
                    }
                }
            }
        }

        val tabTitles = listOf("Casos de Clientes", "Red de Colegas")
        val selectedTabIndex = if (filtroTipoConsulta == "cliente") 0 else 1
        
        TabRow(
            selectedTabIndex = selectedTabIndex,
            modifier = Modifier.fillMaxWidth()
        ) {
            tabTitles.forEachIndexed { index, title ->
                Tab(
                    selected = selectedTabIndex == index,
                    onClick = {
                        val newType = if (index == 0) "cliente" else "colegiado"
                        feedViewModel.setFiltroTipoConsulta(newType)
                    },
                    text = { Text(title, fontWeight = if (selectedTabIndex == index) FontWeight.Bold else FontWeight.Normal) }
                )
            }
        }

        val isVerificadoNivel3 = feedViewModel.isUsuarioVerificadoNivel3()
        var showRequiereVerificacionDialog by remember { mutableStateOf(false) }

        if (filtroTipoConsulta == "colegiado") {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp, vertical = 12.dp),
                contentAlignment = Alignment.CenterEnd
            ) {
                ExtendedFloatingActionButton(
                    onClick = { 
                        if (isVerificadoNivel3) {
                            navController.navigate("${Routes.PUBLICAR_CONSULTA}?tipoConsulta=colegiado")
                        } else {
                            showRequiereVerificacionDialog = true
                        }
                    },
                    icon = { Icon(Icons.Default.Add, contentDescription = "Nueva Interconsulta B2B") },
                    text = { Text("Nueva Interconsulta B2B", fontWeight = FontWeight.Bold) },
                    containerColor = Color(0xFF283593),
                    contentColor = Color.White
                )
            }
        }

        if (showRequiereVerificacionDialog) {
            AlertDialog(
                onDismissRequest = { showRequiereVerificacionDialog = false },
                icon = { Icon(Icons.Default.VerifiedUser, contentDescription = null, tint = Color(0xFFE65100)) },
                title = { Text("Verificación Requerida (Nivel 3)") },
                text = {
                    Text("Para publicar o colaborar en la Red de Colegas B2B debes contar con el perfil verificado por nuestro equipo técnico (Nivel 3 Verificado).")
                },
                confirmButton = {
                    Button(
                        onClick = { showRequiereVerificacionDialog = false },
                        colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF283593))
                    ) {
                        Text("Entendido")
                    }
                }
            )
        }

        // ── Si no está logueado, mostrar prompt ──────────
        if (!isLoggedIn) {
            Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                Card(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(24.dp),
                    shape = RoundedCornerShape(16.dp),
                    colors = CardDefaults.cardColors(
                        containerColor = VerdeAbogado.copy(alpha = 0.05f)
                    )
                ) {
                    Column(
                        modifier = Modifier.padding(24.dp),
                        verticalArrangement = Arrangement.spacedBy(12.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Icon(
                            Icons.Default.Lock,
                            null,
                            tint = VerdeAbogado,
                            modifier = Modifier.size(48.dp)
                        )
                        Text(
                            "Acceso Restringido",
                            fontWeight = FontWeight.Bold,
                            color = VerdeAbogado,
                            textAlign = TextAlign.Center
                        )
                        Text(
                            "Iniciá sesión para ver las consultas disponibles y poder responderlas.",
                            fontSize = 13.sp,
                            textAlign = TextAlign.Center
                        )
                        Button(
                            onClick = { 
                                navController.navigate(Routes.login(register = false)) 
                            },
                            modifier = Modifier.fillMaxWidth().height(50.dp),
                            colors = ButtonDefaults.buttonColors(containerColor = VerdeAbogado),
                            shape = RoundedCornerShape(12.dp)
                        ) {
                            Text("Iniciar Sesión", fontWeight = FontWeight.Bold)
                        }
                    }
                }
            }
            return@Column
        }

        // ── UI DE CUARENTENA: Perfil Profesional en Revisión (Nivel 2) ──────
        val perfilState by viewModel.perfilAbogadoState.collectAsState()
        val perfilAbogadoData = (perfilState as? UiState.Success<*>)?.data as? com.rosario.juridico.domain.PerfilAbogadoDataModel

        val isPendienteVerificacion = !isVerificadoNivel3 && (currentUserAbogado?.nivelVerificacion == com.rosario.juridico.marketplace.model.NivelVerificacion.NIVEL_2_PENDIENTE.name ||
                perfilAbogadoData?.nivelVerificacion == com.rosario.juridico.marketplace.model.NivelVerificacion.NIVEL_2_PENDIENTE.name)

        if (isPendienteVerificacion) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(24.dp),
                contentAlignment = Alignment.Center
            ) {
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    shape = RoundedCornerShape(20.dp),
                    colors = CardDefaults.cardColors(
                        containerColor = MaterialTheme.colorScheme.surface
                    ),
                    elevation = CardDefaults.cardElevation(defaultElevation = 4.dp)
                ) {
                    Column(
                        modifier = Modifier.padding(28.dp),
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(16.dp)
                    ) {
                        Box(
                            modifier = Modifier
                                .size(72.dp)
                                .background(Color(0xFFE65100).copy(alpha = 0.1f), androidx.compose.foundation.shape.CircleShape),
                            contentAlignment = Alignment.Center
                        ) {
                            Icon(
                                imageVector = Icons.Default.HourglassTop,
                                contentDescription = "Perfil en Revisión",
                                tint = Color(0xFFE65100),
                                modifier = Modifier.size(36.dp)
                            )
                        }

                        Text(
                            text = "Perfil Profesional en Revisión",
                            style = MaterialTheme.typography.titleLarge.copy(fontWeight = FontWeight.Bold),
                            color = MaterialTheme.colorScheme.onSurface,
                            textAlign = TextAlign.Center
                        )

                        Surface(
                            shape = RoundedCornerShape(8.dp),
                            color = Color(0xFFFFF3E0)
                        ) {
                            Text(
                                text = "ESTADO: EN REVISIÓN (NIVEL 2)",
                                modifier = Modifier.padding(horizontal = 12.dp, vertical = 6.dp),
                                style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold),
                                color = Color(0xFFE65100)
                            )
                        }

                        Text(
                            text = "Tu perfil está en revisión por nuestro equipo técnico. Una vez validada tu matrícula, tendrás acceso completo a las oportunidades del Marketplace.",
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            textAlign = TextAlign.Center
                        )

                        if (!currentUserAbogado?.matricula.isNullOrBlank()) {
                            Text(
                                text = "Matrícula enviada: ${currentUserAbogado?.matricula}",
                                style = MaterialTheme.typography.bodySmall.copy(fontWeight = FontWeight.SemiBold),
                                color = MaterialTheme.colorScheme.primary
                            )
                        }

                        OutlinedButton(
                            onClick = {
                                viewModel.cargarPerfilAbogado()
                                viewModel.loadCurrentUserAbogado()
                            },
                            modifier = Modifier.fillMaxWidth().height(48.dp),
                            shape = RoundedCornerShape(12.dp)
                        ) {
                            Icon(Icons.Default.Refresh, contentDescription = null, modifier = Modifier.size(18.dp))
                            Spacer(Modifier.width(8.dp))
                            Text("Comprobar Estado de Verificación")
                        }
                    }
                }
            }
            return@Column
        }

        // ── Banner de Suscripción (si no está suscripto) ──────────────────────
        if (!isSubscribed) {
            Card(
                modifier = Modifier.fillMaxWidth().padding(16.dp),
                colors = CardDefaults.cardColors(containerColor = VerdeAbogado.copy(alpha = 0.1f)),
                shape = RoundedCornerShape(12.dp)
            ) {
                Row(
                    modifier = Modifier.padding(12.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Icon(Icons.Default.Lock, null, tint = VerdeAbogado)
                    Column(modifier = Modifier.weight(1f)) {
                        Text("Acceso Limitado", fontWeight = FontWeight.Bold, fontSize = 14.sp)
                        Text("Suscribite para ver datos de contacto y responder.", fontSize = 12.sp)
                    }
                    Button(
                        onClick = { navController.navigate(Routes.REGISTRO_ABOGADO) },
                        colors = ButtonDefaults.buttonColors(containerColor = VerdeAbogado),
                        contentPadding = PaddingValues(horizontal = 12.dp, vertical = 4.dp),
                        shape = RoundedCornerShape(8.dp)
                    ) {
                        Text("Suscribirme", fontSize = 12.sp)
                    }
                }
            }
        }



        // ── Lista ─────────────────────────────────────────────────────────────
        when (val s = feedState) {
            is UiState.Success -> {
                val feedList = s.data
                if (feedList.isNotEmpty()) {
                    LazyColumn(
                        modifier = Modifier.fillMaxSize(),
                        contentPadding = PaddingValues(horizontal = 16.dp, vertical = 4.dp),
                        verticalArrangement = Arrangement.spacedBy(10.dp)
                    ) {
                        items(feedList) { consulta ->
                            ConsultaDataCard(consulta = consulta, isSubscribed = isSubscribed) {
                                navController.navigate(Routes.detalleConsulta(consulta.id ?: ""))
                            }
                        }
                        item { Spacer(Modifier.height(16.dp)) }
                    }
                } else {
                    Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                        Column(
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            Icon(
                                Icons.Default.SearchOff,
                                null,
                                modifier = Modifier.size(48.dp),
                                tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f)
                            )
                            Text(
                                "No hay consultas abiertas",
                                style = MaterialTheme.typography.bodyLarge,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                            Text(
                                "Vuelve a consultar más tarde para ver nuevas publicaciones.",
                                style = MaterialTheme.typography.bodySmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f)
                            )
                        }
                    }
                }
            }
            is UiState.Loading -> {
                Box(
                    modifier = Modifier.fillMaxSize(),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator(color = VerdeAbogado)
                }
            }
            else -> {
                Box(modifier = Modifier.fillMaxSize(), contentAlignment = Alignment.Center) {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Icon(
                            Icons.Default.SearchOff,
                            null,
                            modifier = Modifier.size(48.dp),
                            tint = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.4f)
                        )
                        Text(
                            "No hay consultas abiertas",
                            style = MaterialTheme.typography.bodyLarge,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                        Text(
                            "Vuelve a consultar más tarde para ver nuevas publicaciones.",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f)
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun ConsultaDataCard(
    consulta: ConsultaDataModel,
    isSubscribed: Boolean,
    onClick: () -> Unit
) {
    val effectiveIsSubscribed = isSubscribed
    val isB2B = (consulta.tipoConsulta ?: "cliente") == "colegiado"
    val cardColor = if (isB2B) Color(0xFF283593).copy(alpha = 0.04f) else MaterialTheme.colorScheme.surface

    Card(
        modifier  = Modifier.fillMaxWidth().clickable { onClick() },
        shape     = RoundedCornerShape(20.dp),
        colors    = CardDefaults.cardColors(containerColor = cardColor),
        elevation = CardDefaults.cardElevation(defaultElevation = 2.dp)
    ) {
        Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                if (isB2B) {
                    Surface(
                        shape = RoundedCornerShape(8.dp),
                        color = Color(0xFF283593)
                    ) {
                        Row(
                            modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(4.dp)
                        ) {
                            Icon(Icons.Default.Handshake, null, tint = Color.White, modifier = Modifier.size(14.dp))
                            Text(
                                text = (consulta.subtipoB2B ?: "INTERCONSULTA B2B").uppercase(),
                                style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold),
                                color = Color.White
                            )
                        }
                    }
                } else {
                    Surface(
                        shape = RoundedCornerShape(8.dp),
                        color = VerdeAbogado.copy(alpha = 0.1f)
                    ) {
                        Text(
                            text = "Consulta Legal",
                            modifier = Modifier.padding(horizontal = 10.dp, vertical = 4.dp),
                            style = MaterialTheme.typography.labelMedium.copy(fontWeight = FontWeight.Bold),
                            color = VerdeAbogado
                        )
                    }
                }

                Surface(
                    shape = RoundedCornerShape(8.dp),
                    color = MaterialTheme.colorScheme.surfaceVariant
                ) {
                    Text(
                        text = consulta.estado ?: "pendiente",
                        modifier = Modifier.padding(horizontal = 8.dp, vertical = 2.dp),
                        style = MaterialTheme.typography.labelSmall,
                        color = MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }

            val descText = consulta.descripcion ?: ""
            val previewText = if (effectiveIsSubscribed) {
                descText
            } else {
                if (descText.length > 70) {
                    descText.take(70) + "..."
                } else {
                    descText
                }
            }

            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                Text(
                    text = previewText,
                    maxLines = 3,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurface
                )
                if (!effectiveIsSubscribed) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Icon(Icons.Default.Lock, null, tint = VerdeAbogado, modifier = Modifier.size(12.dp))
                        Spacer(Modifier.width(4.dp))
                        Text(
                            "Ver más (solo suscriptos)",
                            style = MaterialTheme.typography.labelSmall.copy(fontWeight = FontWeight.Bold),
                            color = VerdeAbogado
                        )
                    }
                }
            }

            // Badges B2B de Jurisdicción Destino y Modalidad de Honorarios
            if (isB2B) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    if (!consulta.jurisdiccionDestino.isNullOrBlank()) {
                        Surface(shape = RoundedCornerShape(6.dp), color = Color(0xFF283593).copy(alpha = 0.1f)) {
                            Row(
                                modifier = Modifier.padding(horizontal = 8.dp, vertical = 3.dp),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(4.dp)
                            ) {
                                Icon(Icons.Default.Place, null, tint = Color(0xFF283593), modifier = Modifier.size(12.dp))
                                Text(
                                    text = consulta.jurisdiccionDestino!!,
                                    style = MaterialTheme.typography.labelSmall.copy(fontWeight = FontWeight.Bold),
                                    color = Color(0xFF283593)
                                )
                            }
                        }
                    }

                    if (!consulta.modalidadHonorarios.isNullOrBlank()) {
                        Surface(shape = RoundedCornerShape(6.dp), color = VerdeAbogado.copy(alpha = 0.1f)) {
                            Row(
                                modifier = Modifier.padding(horizontal = 8.dp, vertical = 3.dp),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(4.dp)
                            ) {
                                Icon(Icons.Default.Payments, null, tint = VerdeAbogado, modifier = Modifier.size(12.dp))
                                Text(
                                    text = "Honorarios: ${consulta.modalidadHonorarios}",
                                    style = MaterialTheme.typography.labelSmall.copy(fontWeight = FontWeight.Bold),
                                    color = VerdeAbogado
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}
